import { Pipe, PipeTransform } from '@angular/core';
import { NotificationService } from '@sharedServices/notification.service';
import imageCompression from 'browser-image-compression';
import { environment } from '@environment';

@Pipe({
  name: 'compressEncodeImage'
})

export class CompressEncodeImagePipe implements PipeTransform {

  public imageCompressionProgress = {
    totalImagesCompressed: null,
    currentImageName: ''
  }

  constructor(
    private notificationService: NotificationService
  ) {}

  /**
   * transform
   * @example
   * transform(imgList, { progress: {
   *       onProgress: (compressionProgress) => {
   *         this.imgCompressionProgress = compressionProgress
   *         this.totalImgCompressed = this.compressEncodeImagePipe.imageCompressionProgress.totalImagesCompressed
   *         this.currentImgName = this.compressEncodeImagePipe.imageCompressionProgress.currentImageName
   *       }}, maxImgUploadSize: 6, maxImgCompressionSize: 7
   *     })
   *
   *
   * @param imageList - Array of image(s) to be compressed & encoded
   * @param options - Additional options to get compression status & to configure global max size(s) for files (optional)
   * @return any[] - Array containing { compressedBase64, compressedImage & originalImage }
   */
  transform(imageList: File[] | FileList, options?: {progress?: {}, maxImgCompressionSize?: number, maxImgUploadSize?: number}): Promise<any[]> {
    this.imageCompressionProgress.totalImagesCompressed = 1
    this.imageCompressionProgress.currentImageName = ''

    let promiseArr: any = [],
        maxImgUploadSize: number = options?.maxImgUploadSize || environment.max_img_upload_size

    const compressionOptions = {
      maxSizeMB: options?.maxImgCompressionSize || environment.max_img_compression_size,
      useWebWorker: true,
      ...options?.progress
    }

    return new Promise((resolve) => {
      Array.from(imageList).forEach((image: File) => {
        const promise = new Promise((resolve, reject) => {
          imageCompression(image, compressionOptions).then((compressedImg: File) => {
            if(compressedImg.size > (maxImgUploadSize * 1048576)) {
              this.notificationService.showWarning(`Skipping file "${compressedImg.name}" as it exceeds upload limit of ${maxImgUploadSize}MB...`, 'Upload Limit Exceeded')
              reject(`${compressedImg.name} exceeds upload limit of ${maxImgUploadSize}MB`)
            } else {
              imageCompression.getDataUrlFromFile(compressedImg).then((base64Str: string) => {
                imageCompression.loadImage(base64Str).then(() => {
                  const responseObj = {
                    compressedBase64: base64Str.split(',')[1],
                    compressedImage: compressedImg,
                    originalImage: image
                  }
                  resolve(responseObj)
                })
              })
            }
          }).then(() => {
            this.imageCompressionProgress.totalImagesCompressed++
            this.imageCompressionProgress.currentImageName = image.name
          }).catch(() => {
            this.notificationService.showWarning(`Skipping "${image.name}" as file is corrupted/damaged`, 'Encoding Error')
            reject(`${image.name} is corrupted/damaged`)
          })
        })
        promiseArr.push(promise)
      })
      Promise.allSettled(promiseArr).then((response: any) => {
        resolve(response.filter(value => value.status === 'fulfilled'))
      })
    })
  }
}
